// ─── Space Route (canonical friendly URL) ───
// `/app/s/{slug}-{short}` — ADR 0012 D3 (Phase 6.5 item 2). Spaces have no
// dedicated dashboard today (out of scope for a URL change) — this route is a
// real, resolvable, bookmarkable URL whose loader scope-checks + self-heals
// the slug (same flow as `/app/l/$listRef`), then renders the app shell with
// the space highlighted/expanded in the sidebar via the existing
// `activeSpaceId` mechanism (the same one Global Search space-results already
// use — see features/search/useNavigateToSearchResult.ts). It does NOT
// redirect to `/app`, so the canonical space URL stays in the address bar. A
// future dedicated space dashboard can render at this same URL without any
// grammar change.

import { useEffect, type ReactElement } from 'react';
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { parseEntityRef } from '@a8-planner/shared/utils/entity-url';
import type { ResolvedEntity } from '@a8-planner/shared';
import { spacesApi } from '../../../lib/api/spaces';
import { workspaceApi } from '../../../lib/api/workspaces';
import { resolveEntity } from '../../../lib/entity-resolve';
import { useUIStore } from '../../../stores/ui.store';
import { Breadcrumb, type BreadcrumbSegment } from '../../../features/layout/Breadcrumb';

// ─── Route definition ───
// loader — identical parse → resolve → canonical-redirect-on-slug-drift flow
// as `/app/l/$listRef` (ADR 0012 D3), just with `type=space`.

export const Route = createFileRoute('/app/s/$spaceRef')({
  loader: async ({ params }): Promise<ResolvedEntity> => {
    const parsed = parseEntityRef(params.spaceRef);
    if (!parsed) {
      throw notFound();
    }

    const resolved = await resolveEntity('space', parsed.short, parsed.slug);

    if (parsed.slug !== resolved.canonicalSlug) {
      throw redirect({
        to: resolved.canonicalPath as '/app',
        search: (prev) => prev,
        replace: true,
      });
    }

    return resolved;
  },
  component: SpaceDetailPage,
});

// ─── Page component ───
// v1 renders only a sidebar highlight (ADR consequence note) — no dedicated
// space dashboard. The empty-state copy mirrors routes/app/index.tsx's
// existing welcome pattern, personalized with the resolved space's name.

function SpaceDetailPage(): ReactElement {
  // FIX 9 (panel review, GHE #94 — pre-existing twin of U15) — this page's comment already
  // says it mirrors routes/app/index.tsx's welcome copy; it hardcoded the exact English literal
  // U15 moved to `common.json`'s `appWelcome.description` instead of reusing that key, plus an
  // untranslated `?? 'Space'` fallback name. Both moved to i18n here.
  const { t } = useTranslation('common');
  const resolved = Route.useLoaderData();
  const setActiveSpaceId = useUIStore((s) => s.setActiveSpaceId);
  const pushRecent = useUIStore((s) => s.pushRecent);

  useEffect(() => {
    setActiveSpaceId(resolved.spaceId);
  }, [resolved.spaceId, setActiveSpaceId]);

  const { data: spaceData } = useQuery({
    queryKey: ['spaces', resolved.spaceId],
    queryFn: () => spacesApi.getById(resolved.spaceId),
  });

  // GHE #283 item 2 — Breadcrumb was wired into exactly one route
  // (`/app/l/$listRef`); this one had no path at all.
  const { data: workspaceData } = useQuery({
    queryKey: ['workspaces', resolved.workspaceId],
    queryFn: () => workspaceApi.getById(resolved.workspaceId),
  });

  const spaceName = spaceData?.data?.name ?? t('appWelcome.spaceFallbackName');
  const workspaceName = workspaceData?.data?.name ?? t('breadcrumb.workspaceFallbackName');

  // #185 — record the visit in Recents once the real name has loaded (never the fallback).
  // #325 — a SPACE entry carries no context: the only thing above it is the workspace, which is
  // exactly what was dropped as redundant. The space name alone is already unambiguous.
  useEffect(() => {
    const name = spaceData?.data?.name;
    if (name) pushRecent({ type: 'SPACE', id: resolved.spaceId, name });
  }, [resolved.spaceId, spaceData?.data?.name, pushRecent]);

  const breadcrumbSegments: BreadcrumbSegment[] = [{ label: workspaceName }, { label: spaceName }];

  return (
    <div className="flex flex-col">
      <Breadcrumb segments={breadcrumbSegments} />
      <div className="flex flex-col items-center justify-center py-20">
        <Globe className="h-16 w-16 text-[var(--text-tertiary)]" aria-hidden="true" />
        {/* `h1`, no `h2` (GHE #1186): es el título de SU página, no una sección
            dentro de otra cosa. Empezar la jerarquía en h2 deja un hueco que un lector
            de pantalla lee como «falta un nivel». */}
        <h1 className="mt-4 text-xl font-semibold text-[var(--text-primary)]">{spaceName}</h1>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">{t('appWelcome.description')}</p>
      </div>
    </div>
  );
}
